import { takeEvery, put, all, select } from 'redux-saga/effects';
import { push, replace } from 'connected-react-router';
import { openModalDialog } from '@nextiva/crm-modals';
import { listActions, listReducer } from '@nextiva/engagement-ui';
import { castArray } from 'lodash';
import { SURVEY_SECTION_LANDING } from 'sharedParts';

import { SURVEY_COMMON_TYPE } from 'constants/SurveyTypes';
import {
  navigateToTemplateEditor,
  navigateToSurveyEditor,
  postDuplicateTemplate,
  deleteTemplate,
  loadTemplateList,
  useTemplate,
} from 'actions/templateListActions';
import { FAVORITE, PLATFORM, MY } from 'constants/templateList/TemplateTabKeys';
import modalTypes from 'constants/AppModalTypes';
import * as actions from 'constants/templateList/TemplateListActions';
import templateInitialProps from 'config/templateList/TemplateListUniqueInitialProps';
import * as templateApi from 'api/templateList';

import { surveyTemplateEditorRoot, surveyEditorRoot } from '../routes/paths';
import { TEMPLATE_TYPE } from '../config/EntityTypeList';
import createQuickViewModalPath from '../domains/quickView/utils/createQuickViewModalPath';
import { setOverwriteCustomStyles } from '../actions/surveyEditorActions';

const {
  loadItemList,
  mountItemList,
  toggleTileFavorite,
  setItemListTab,
  loadItemListPreviewOptions,
  setItemListFilterSearch,
} = listActions;

const {
  getItemListSelectedItemsIds,
  getItemListTabName,
  getListItemSearchText,
  getItemListPageNumber,
  getSortBy,
  getSortOrder,
} = listReducer;

function* onCreateTemplateSuccess({ data: { id } }) {
  yield put(navigateToTemplateEditor(id));
}

function* onTemplateListActionUse({ payload: listId }) {
  const state = yield select();
  const selectedId = getItemListSelectedItemsIds(state, listId)[0];
  yield put(useTemplate(listId, selectedId));
}

function* onTemplateListActionEdit({ payload: listId }) {
  const state = yield select();
  const selectedId = getItemListSelectedItemsIds(state, listId)[0];
  yield put(navigateToTemplateEditor(selectedId));
}

function* onTemplateListActionDuplicate({ payload: listId }) {
  const state = yield select();
  const selectedId = getItemListSelectedItemsIds(state, listId)[0];
  yield put(postDuplicateTemplate({ listId, isCommon: false }, selectedId));
}

function* onTemplateListActionOpenDeleteModal({ payload: listId }) {
  const state = yield select();
  const selectedIds = getItemListSelectedItemsIds(state, listId);
  yield put(
    openModalDialog({
      type: modalTypes.deleteTemplate,
      templateIds: selectedIds,
    }),
  );
}

function* onTemplateListActionDelete({ payload: { listId, itemIds } }) {
  const deleteTemplatesQueue = castArray(itemIds).map(id =>
    templateApi.deleteTemplate(id),
  );
  yield put(
    deleteTemplate(listId, () => Promise.all(deleteTemplatesQueue), itemIds),
  );
}

function* onDeleteTemplateSuccess({ listId }) {
  yield put(loadTemplateList(listId, { pageNumber: 0 }));
}

function* onNavigateToTemplateEditor({ payload: templateId }) {
  yield put(push(`${surveyTemplateEditorRoot}/${templateId}/landing`));
}

function* onNavigateToTemplateQuickView({ payload: listId }) {
  const selectedIdList = yield select(getItemListSelectedItemsIds, listId);
  yield put(
    push(
      createQuickViewModalPath(selectedIdList[0], {
        entityType: TEMPLATE_TYPE,
      }),
    ),
  );
}

function* onNavigateToSurveyEditor({ payload }) {
  const redirectUrl = `${surveyEditorRoot}/${
    payload.id
  }/${SURVEY_SECTION_LANDING}`;

  if (payload.shouldReplaceLocation) {
    yield put(replace(redirectUrl));
  } else {
    yield put(push(redirectUrl));
  }
}

function* onLoadTemplateList({ payload: { listId, params } }) {
  const tabName = yield select(getItemListTabName, listId);
  const search = yield select(getListItemSearchText, listId);
  const pageNumber = yield select(getItemListPageNumber, listId);
  const sortOrder = yield select(getSortOrder, listId);
  const sortBy = yield select(getSortBy, listId);

  yield put(
    loadItemList(listId, templateApi.fetchTemplateList, {
      search,
      onlyFavorites: tabName === FAVORITE,
      onlyPlatform: tabName === PLATFORM,
      onlyMy: tabName === MY,
      pageNumber,
      sortBy,
      sortOrder,
      ...params,
    }),
  );

  yield put(setOverwriteCustomStyles(false));
}

function* onMountTemplateList({ payload: { listId, listInitialProps } }) {
  yield put(mountItemList(listId, listInitialProps || templateInitialProps));
}

function* onToggleTemplateTileFavorite({
  payload: { listId, templateId, isFavorite },
}) {
  yield put(
    toggleTileFavorite(
      listId,
      templateApi.favoriteTemplate,
      templateId,
      isFavorite,
    ),
  );
}

function* onTemplateTabChange({ payload: { listId, tabName, page = 0 } }) {
  yield put(setItemListTab(listId, tabName));
  yield put(
    loadTemplateList(listId, {
      pageNumber: page,
      onlyFavorites: tabName === FAVORITE,
      onlyPlatform: tabName === PLATFORM,
      onlyMy: tabName === MY,
    }),
  );
}

function* onLoadTemplateListPreviewOptions({ payload: { listId, text } }) {
  yield put(
    loadItemListPreviewOptions(listId, templateApi.getTemplateNames, text),
  );
}

function* onTemplateSearch({ payload: { listId, text } }) {
  yield put(setItemListFilterSearch(listId, text));
  yield put(loadTemplateList(listId, { pageNumber: 0 }));
}

function* onPostDuplicateTemplateSuccess({
  isCommon,
  shouldReplaceLocation,
  data: { id },
}) {
  yield put(
    isCommon
      ? navigateToSurveyEditor(id, shouldReplaceLocation)
      : navigateToTemplateEditor(id),
  );
}

function* onUseTemplate({
  payload: { listId, itemId, shouldReplaceLocation },
}) {
  yield put(
    postDuplicateTemplate(
      {
        listId,
        isCommon: true,
        shouldReplaceLocation,
      },
      itemId,
      { type_: SURVEY_COMMON_TYPE },
    ),
  );
}

function* onSortTemplateListColumn({ payload: { listId, field, order } }) {
  const currentTabName = yield select(getItemListTabName, listId);
  yield put(
    loadTemplateList(listId, {
      pageNumber: 0,
      onlyFavorites: currentTabName === FAVORITE,
      sortBy: field,
      sortOrder: order,
    }),
  );
}

export default function*() {
  yield all([
    takeEvery(
      actions.NAVIGATE_TO_TEMPLATE_QUICK_VIEW,
      onNavigateToTemplateQuickView,
    ),
    takeEvery(actions.POST_CREATE_TEMPLATE_SUCCESS, onCreateTemplateSuccess),
    takeEvery(actions.TEMPLATE_LIST_ACTION_USE, onTemplateListActionUse),
    takeEvery(actions.TEMPLATE_LIST_ACTION_EDIT, onTemplateListActionEdit),
    takeEvery(actions.TEMPLATE_LIST_ACTION_DELETE, onTemplateListActionDelete),
    takeEvery(
      actions.TEMPLATE_LIST_ACTION_DUPLICATE,
      onTemplateListActionDuplicate,
    ),
    takeEvery(
      actions.TEMPLATE_LIST_ACTION_OPEN_DELETE_MODAL,
      onTemplateListActionOpenDeleteModal,
    ),
    takeEvery(actions.DELETE_TEMPLATE_SUCCESS, onDeleteTemplateSuccess),
    takeEvery(actions.NAVIGATE_TO_TEMPLATE_EDITOR, onNavigateToTemplateEditor),
    takeEvery(actions.NAVIGATE_TO_SURVEY_EDITOR, onNavigateToSurveyEditor),
    takeEvery(actions.LOAD_TEMPLATE_LIST, onLoadTemplateList),
    takeEvery(actions.MOUNT_TEMPLATE_LIST, onMountTemplateList),
    takeEvery(
      actions.TOGGLE_TEMPLATE_TILE_FAVORITE,
      onToggleTemplateTileFavorite,
    ),
    takeEvery(actions.TEMPLATE_TAB_CHANGE, onTemplateTabChange),
    takeEvery(
      actions.LOAD_TEMPLATE_LIST_PREVIEW_OPTIONS,
      onLoadTemplateListPreviewOptions,
    ),
    takeEvery(actions.ON_TEMPLATE_SEARCH, onTemplateSearch),
    takeEvery(
      actions.POST_DUPLICATE_TEMPLATE_SUCCESS,
      onPostDuplicateTemplateSuccess,
    ),
    takeEvery(actions.USE_TEMPLATE, onUseTemplate),
    takeEvery(actions.SORT_TEMPLATE_LIST_COLUMN, onSortTemplateListColumn),
  ]);
}
